import React, { useState, useEffect, useCallback } from 'react';
import { Download, TrendingUp, TrendingDown } from 'lucide-react';
import apiService from '../../../apiService';
import { exportToCSV } from '../../../utils/adminHelpers';
import { ACTIVITY_META, ACTIVITY_ORDER, fmtMoney, fmtInt, Card, KpiCard, BarRow, Spinner, usePeriodControls } from '../financeShared';

export default function OverviewTab({ currency, rate }) {
  const { params, qs, groupBy, controls } = usePeriodControls('1year', 'month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (n) => fmtMoney(n, currency, rate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`/admin/finance/overview?${qs()}`, { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('overview:', e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals;
  const byActivity = totals?.byActivity || {};
  const volumes = data?.volumes || {};
  const timeline = data?.timeline || [];

  const exportCSV = () => {
    exportToCSV('pilotage_revenus', ACTIVITY_ORDER.map(k => ({
      Activité: ACTIVITY_META[k].label,
      [`Montant (${currency})`]: currency === 'USD' ? (byActivity[k] || 0) / rate : (byActivity[k] || 0),
      'Part %': totals?.revenue > 0 ? (((byActivity[k] || 0) / totals.revenue) * 100).toFixed(1) : '0',
    })));
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        {controls()}
        <button className="btn-secondary" onClick={exportCSV} style={{ padding: '0.4rem 1rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: '1.25rem' }}>
          <Download size={15} /> Exporter CSV
        </button>
      </div>

      {loading ? <Spinner /> : !totals ? <p style={{ color: '#9AA0A8' }}>Aucune donnée.</p> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
            <KpiCard label="Chiffre d'affaires" value={fmt(totals.revenue)} color="#10b981" border="#10b981" />
            <KpiCard label="Marge brute plateforme" value={fmt(totals.grossMargin)} color="#FF7800" border="#FF7800" sub="CA − reversements partenaires" />
            <KpiCard label="Reversé aux partenaires" value={fmt(totals.partnerPayouts)} color="#FFB900" border="#FFB900" />
            <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #FF7800' }}>
              <p style={{ fontSize: '0.75rem', color: '#5C636E', marginBottom: '0.4rem', textTransform: 'uppercase', fontWeight: 600 }}>Croissance vs période préc.</p>
              <p style={{ fontSize: '1.6rem', fontWeight: 700, margin: '0.25rem 0', display: 'flex', alignItems: 'center', gap: 6,
                color: totals.growthPct == null ? '#5C636E' : (totals.growthPct >= 0 ? '#10b981' : '#dc2626') }}>
                {totals.growthPct != null && (totals.growthPct >= 0 ? <TrendingUp size={22} /> : <TrendingDown size={22} />)}
                {totals.growthPct == null ? '—' : `${totals.growthPct > 0 ? '+' : ''}${totals.growthPct}%`}
              </p>
            </div>
          </div>

          <Card style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.25rem', color: '#1E2229', fontSize: '1.05rem', fontWeight: 600 }}>Répartition du CA par activité</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              {ACTIVITY_ORDER.map(k => {
                const amount = byActivity[k] || 0;
                const pct = totals.revenue > 0 ? (amount / totals.revenue) * 100 : 0;
                return <BarRow key={k} label={ACTIVITY_META[k].label} pct={pct} color={ACTIVITY_META[k].color} valueText={fmt(amount)} />;
              })}
            </div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {[
              { label: 'Courses', value: volumes.rides }, { label: 'Livraisons', value: volumes.deliveries },
              { label: 'Locations', value: volumes.rentals }, { label: 'Réservations', value: volumes.bookings },
              { label: 'Abonnements', value: volumes.subscriptions },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: 'white', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1E2229', margin: 0 }}>{fmtInt(value)}</p>
                <p style={{ fontSize: '0.75rem', color: '#5C636E', margin: '0.2rem 0 0', textTransform: 'uppercase', fontWeight: 600 }}>{label}</p>
              </div>
            ))}
          </div>

          <Card>
            <h3 style={{ marginBottom: '1.25rem', color: '#1E2229', fontSize: '1.05rem', fontWeight: 600 }}>Évolution du CA ({groupBy === 'month' ? 'par mois' : 'par jour'})</h3>
            {timeline.length === 0 ? <p style={{ color: '#9AA0A8' }}>Aucune donnée sur la période.</p> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                    {['Période', 'CA total', 'dont commissions', 'dont abonnements'].map(h => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h === 'Période' ? 'left' : 'right', fontWeight: 700, color: '#414852', fontSize: '0.8rem', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {timeline.slice().reverse().map((row, idx) => (
                      <tr key={row.period} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#fff' : '#FFFFFF' }}>
                        <td style={{ padding: '0.7rem 1rem', color: '#1E2229', fontWeight: 600 }}>{row.period}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#10b981', fontWeight: 700 }}>{fmt(row.revenue)}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#FF7800' }}>{fmt(row.commissions)}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#FF7800' }}>{fmt(row.subscriptions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
